import React, { useState, useCallback } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import {
  PositionData,
  ClosedPositionData,
} from "@crypto-trading/common/browser";
import { Loader2, Plus } from "lucide-react";
import { DesktopLayout } from "@/layouts/DesktopLayout";
import {
  ExchangeFilterSelect,
  ExchangeFilterOption,
} from "@/components/ExchangeFilterSelect";
import { ExchangeConnectList } from "@/components/ExchangeConnectList";
import { AutotradeTriggerManager } from "@/components/AutotradeTriggerManager";
import { useOpenPositions } from "@/hooks/usePositions";
import { trackEvent } from "@/utils/analytics";

// Desktop drawer components
import {
  PositionDetailDrawerDesktop,
  PositionTradeDrawerDesktop,
  TickerSymbolDrawerDesktop,
} from "./drawers";
import { PositionTile } from "@/components/PositionTile";

export const AllPositionsPageDesktop: React.FC = () => {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [selectedPosition, setSelectedPosition] = useState<
    PositionData | ClosedPositionData | null
  >(null);
  const [isTickerDrawerOpen, setIsTickerDrawerOpen] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [exchangeFilter, setExchangeFilter] =
    useState<ExchangeFilterOption>("all");

  // React Query hooks - only for open positions
  const exchangeCodeFilter =
    exchangeFilter === "all" ? undefined : exchangeFilter;
  const { data: openPositions = [], isLoading } = useOpenPositions(
    userId,
    true,
    exchangeCodeFilter
  );

  const handleOpenTickerDrawer = () => {
    trackEvent("custom_trade_attempt", { page: "positions", userId });
    setIsTickerDrawerOpen(true);
  };

  const handleTickerSubmit = useCallback((normalizedSymbol: string) => {
    setTradeSymbol(normalizedSymbol);
    setIsTickerDrawerOpen(false);
    setIsTradeDrawerOpen(true);
  }, []);

  const handleTradeDrawerClose = (open: boolean) => {
    setIsTradeDrawerOpen(open);
    if (!open) setTradeSymbol(null);
  };

  // Right sidebar content - Same as Settings page
  const rightSidebar = (
    <div className="space-y-6 2xl:w-[450px] w-[350px]">
      <ExchangeConnectList userId={userId} compact />
      <AutotradeTriggerManager userId={userId} />
    </div>
  );

  // Header actions - New Trade button
  const headerActions = (
    <button
      onClick={handleOpenTickerDrawer}
      className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white font-medium rounded-lg transition-all duration-150 shadow-lg hover:shadow-accent/20"
    >
      <Plus className="w-4 h-4" />
      <span>New Trade</span>
    </button>
  );

  return (
    <>
      <DesktopLayout
        pageTitle="Trade"
        pageDescription="Manage your open positions"
        rightSidebar={rightSidebar}
        showRightSidebar={true}
        headerActions={headerActions}
      >
        {/* Exchange Filter */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-text-secondary">
            Display position for
          </span>
          <ExchangeFilterSelect
            value={exchangeFilter}
            onChange={setExchangeFilter}
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && openPositions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-text-secondary">No open positions found</p>
          </div>
        )}

        {/* Positions Grid - Multi-column like alerts */}
        {!isLoading && openPositions.length > 0 && (
          <div
            className="grid gap-4 justify-center"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(480px, 700px))",
            }}
          >
            {openPositions.map((position) => (
              <div key={position.positionId} className="w-full max-w-[700px]">
                <PositionTile
                  position={position}
                  onClick={() => setSelectedPosition(position)}
                />
              </div>
            ))}
          </div>
        )}
      </DesktopLayout>

      {/* Desktop Sheets */}
      <TickerSymbolDrawerDesktop
        open={isTickerDrawerOpen}
        onOpenChange={setIsTickerDrawerOpen}
        onSubmit={handleTickerSubmit}
        title="Enter Ticker Symbol"
        submitButtonText="Continue to Trade"
      />

      <PositionTradeDrawerDesktop
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={handleTradeDrawerClose}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
            setIsTradeDrawerOpen(false);
          }
        }}
      />

      <PositionDetailDrawerDesktop
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        onPositionUpdate={(updatedPosition) =>
          setSelectedPosition(updatedPosition)
        }
        source="positions_page"
      />
    </>
  );
};
